import { PublicClientApplication, type Configuration, type SilentRequest } from '@azure/msal-browser'
import { crearRenovadorDeToken } from './reautenticacion'

/**
 * Autenticación de la consola interna de plataforma (RF-107) contra un tenant NORMAL de
 * Entra ID (fuerza de trabajo) — a propósito una instancia de MSAL completamente aparte de
 * `./autenticacion.ts` (el CIAM de los despachos): otro registro de aplicación, otra
 * autoridad, otro almacenamiento de sesión. Un colaborador de un despacho iniciar sesión en
 * la SPA normal no le da ni un ápice de acceso a esta consola, y viceversa — son dos
 * aplicaciones cliente distintas que comparten solo el mismo dominio de despliegue.
 *
 * Ningún valor de aquí es secreto: son identificadores públicos de la SPA de plataforma.
 *
 * Este módulo se importa de forma incondicional desde main.tsx (y transitivamente desde
 * ConsolaDePlataforma.tsx) para decidir en tiempo de ejecución qué instancia de MSAL montar
 * — así que evaluarlo NUNCA debe poder tronar, sin importar el ambiente: si VITE_PLATAFORMA_*
 * no está configurada (como en CI hoy, o en un local sin la consola de plataforma en uso), la
 * SPA de despachos completa dejaría de renderizar por una variable que ni siquiera usa. Por
 * eso la autoridad cae a un valor válido por omisión en vez de dejar que `new URL(undefined)`
 * lance — la consola de plataforma simplemente no funcionará sin la configuración real, igual
 * que antes, pero nunca se lleva de encuentro al resto de la aplicación.
 */
const autoridadDePlataforma = import.meta.env.VITE_PLATAFORMA_AUTHORITY || 'https://login.microsoftonline.com/common/'

/**
 * El regreso de Entra tiene que caer DENTRO de /plataforma, no en la raíz. main.tsx decide qué
 * instancia de MSAL montar mirando window.location.pathname: con el origen a secas, Entra
 * devolvía al usuario a "/", ahí se montaba la SPA de despachos con su propia instancia, y esa
 * no reconoce la respuesta del otro tenant. El síntoma era desconcertante — te autenticabas
 * correctamente contra el tenant de plataforma y acababas viendo "Entrar al despacho".
 *
 * Esta URL debe estar registrada tal cual en el registro de aplicación de la SPA de plataforma
 * (Authentication → Single-page application), incluida la de desarrollo local.
 */
const rutaDeLaConsola = `${window.location.origin}/plataforma`

const configuracionDePlataforma: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_PLATAFORMA_CLIENT_ID,
    authority: autoridadDePlataforma,
    knownAuthorities: [new URL(autoridadDePlataforma).hostname],
    redirectUri: rutaDeLaConsola,
    postLogoutRedirectUri: rutaDeLaConsola,
  },
  cache: {
    // Sesión propia, separada de la del despacho (que también usa sessionStorage pero en su
    // propia instancia de MSAL): cerrar una no afecta a la otra.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
}

export const msalDePlataforma = new PublicClientApplication(configuracionDePlataforma)

export const alcanceApiDePlataforma = import.meta.env.VITE_PLATAFORMA_API_SCOPE

export const peticionDeAccesoDePlataforma = {
  scopes: [alcanceApiDePlataforma],
}

/**
 * Si la renovación silenciosa falla, redirige a Entra en vez de mostrar el error (ver
 * reautenticacion.ts). Marca propia: la sesión de la consola no se mezcla con la del despacho.
 */
const renovarToken = crearRenovadorDeToken(msalDePlataforma, 'contaquick.reautenticacionPlataforma')

/** Obtiene un token de plataforma válido, renovándolo en silencio y volviendo a Entra si la sesión venció. */
export async function obtenerTokenDePlataforma(): Promise<string> {
  const cuenta = msalDePlataforma.getActiveAccount() ?? msalDePlataforma.getAllAccounts()[0]

  if (!cuenta) {
    throw new Error('No hay sesión iniciada en la consola de plataforma.')
  }

  const peticion: SilentRequest = { ...peticionDeAccesoDePlataforma, account: cuenta }

  return renovarToken(peticion)
}
